import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, Users, KeyRound, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';

const INPUT_CLASS = 'h-11 rounded-xl bg-muted/50 border-transparent focus:bg-card focus:border-primary';
const EMPTY = { name: '', email: '', password: '', inviteCode: '' };

const errorText = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((e) => e.msg).join(' ');
    return 'Something went wrong';
};

const Field = ({ id, label, hint, ...props }) => (
    <div className="space-y-1.5">
        <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
        <Input id={id} className={INPUT_CLASS} {...props} />
        {hint && <p className="text-xs text-muted-foreground" id={`${id}-hint`}>{hint}</p>}
    </div>
);

const PathChooser = ({ onPick }) => (
    <div className="p-6 space-y-3" data-testid="signup-path-chooser">
        <button onClick={() => onPick('start')} className="w-full text-left rounded-xl border border-border p-4 hover:border-primary hover:bg-primary/5 transition-colors flex gap-3" data-testid="signup-path-start">
            <Users className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <span><span className="block font-semibold">I&apos;m starting a group</span><span className="text-sm text-muted-foreground">For pastors and group leaders. No invite code needed.</span></span>
        </button>
        <button onClick={() => onPick('join')} className="w-full text-left rounded-xl border border-border p-4 hover:border-primary hover:bg-primary/5 transition-colors flex gap-3" data-testid="signup-path-join">
            <KeyRound className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <span><span className="block font-semibold">I have an invite code</span><span className="text-sm text-muted-foreground">Join a group your leader has already set up.</span></span>
        </button>
    </div>
);

const COPY = {
    login: ['Welcome back', 'Sign in to continue to your group.'],
    register: ['Join The Room', 'How would you like to get started?'],
    start: ['Start your group', 'Create your leader account, then name your group.'],
    join: ['Join your group', 'Use the invite code from your group leader.'],
};

export function AuthModal({ open, onClose, mode, setMode, onGuestDemo }) {
    const { login, register, registerLeader } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (mode !== 'login' && form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
        setLoading(true);
        try {
            if (mode === 'login') {
                await login(form.email, form.password);
                navigate('/dashboard');
            } else if (mode === 'start') {
                await registerLeader({ name: form.name, email: form.email, password: form.password });
                toast.success('Account created! Now name your group.');
                navigate('/teacher-setup');
            } else {
                await register({ name: form.name, email: form.email, password: form.password, invite_code: form.inviteCode.trim().toUpperCase() });
                toast.success('Account created! Your group leader will approve you shortly.');
                setMode('login');
            }
        } catch (err) {
            toast.error(errorText(err));
        } finally {
            setLoading(false);
        }
    };

    const [title, subtitle] = COPY[mode] || COPY.login;
    const isSignup = mode === 'start' || mode === 'join';

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl border-border/50 max-h-[92vh] overflow-y-auto" data-testid="auth-modal">
                <div className="p-6 pb-0">
                    {isSignup && (
                        <button onClick={() => setMode('register')} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2" data-testid="auth-back-to-paths">
                            <ArrowLeft className="w-3 h-3" /> Other options
                        </button>
                    )}
                    <DialogTitle className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{title}</DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground mt-1">{subtitle}</DialogDescription>
                </div>

                {mode === 'register' ? <PathChooser onPick={setMode} /> : (
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {isSignup && (
                            <Field id="auth-name" label="Full name" value={form.name} onChange={set('name')} required autoComplete="name" data-testid="auth-name-input" />
                        )}
                        {mode === 'join' && (
                            <Field id="auth-invite" label="Invite code" value={form.inviteCode} onChange={set('inviteCode')} required autoComplete="off" placeholder="e.g. ABCD1234" data-testid="auth-invite-input" />
                        )}
                        <Field id="auth-email" label="Email" type="email" value={form.email} onChange={set('email')} required autoComplete="email" inputMode="email" data-testid="auth-email-input" />
                        <Field
                            id="auth-password" label="Password" type="password" value={form.password} onChange={set('password')} required
                            autoComplete={isSignup ? 'new-password' : 'current-password'}
                            minLength={isSignup ? 8 : undefined}
                            hint={isSignup ? 'At least 8 characters.' : undefined}
                            aria-describedby={isSignup ? 'auth-password-hint' : undefined}
                            data-testid="auth-password-input"
                        />
                        {mode === 'login' && (
                            <div className="text-right -mt-2">
                                <Link to="/forgot-password" onClick={onClose} className="text-xs text-primary hover:underline" data-testid="auth-forgot-password-link">Forgot password?</Link>
                            </div>
                        )}
                        <Button type="submit" disabled={loading} className="w-full rounded-xl h-11 text-base font-semibold touch-manipulation" data-testid="auth-submit-btn">
                            {loading ? 'Please wait...' : mode === 'login' ? 'Sign in' : mode === 'start' ? 'Create leader account' : 'Join group'}
                        </Button>
                        {isSignup && (
                            <p className="text-xs text-muted-foreground text-center" data-testid="auth-consent-text">
                                By signing up you agree to the{' '}
                                <Link to="/terms" onClick={onClose} className="text-primary underline" data-testid="auth-terms-link">Terms</Link>{' '}and{' '}
                                <Link to="/privacy" onClick={onClose} className="text-primary underline" data-testid="auth-privacy-link">Privacy Policy</Link>.
                            </p>
                        )}
                        {mode === 'login' && (
                            <Button type="button" variant="outline" onClick={() => { onClose(); onGuestDemo(); }} className="w-full rounded-xl h-11 text-sm font-medium" data-testid="auth-try-demo-btn">
                                <Eye className="w-4 h-4 mr-2" /> Try demo (read-only)
                            </Button>
                        )}
                    </form>
                )}

                <div className="px-6 pb-6 text-center">
                    <p className="text-sm text-muted-foreground">
                        {mode === 'login' ? (
                            <>Don&apos;t have an account? <button onClick={() => setMode('register')} className="text-primary font-semibold hover:underline" data-testid="auth-switch-register">Sign up</button></>
                        ) : (
                            <>Already have an account? <button onClick={() => setMode('login')} className="text-primary font-semibold hover:underline" data-testid="auth-switch-login">Log in</button></>
                        )}
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default AuthModal;
